import { useAuth } from "@/hooks/use-auth";

/**
 * Returns the organization's configured IANA timezone string.
 * Falls back to "UTC" if not set or user has no org.
 * 
 * Use this for displaying timestamps (createdAt, completedAt, etc.)
 * For date-only fields (check-in, check-out dates), always use timeZone: "UTC"
 * since those are calendar dates stored without a time component.
 */
export function useTimezone(): string {
  const { user } = useAuth();
  return (user as any)?.orgTimezone || "UTC";
}

/**
 * Format a date string or Date for display using the org's timezone.
 * For full timestamps (ISO strings with time component).
 */
export function formatWithTimezone(
  d: string | Date | null | undefined,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!d) return "-";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
    timeZone: timezone,
  });
}

/** Common IANA timezones with display labels */
export const COMMON_TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET) — New York" },
  { value: "America/Chicago", label: "Central Time (CT) — Chicago" },
  { value: "America/Denver", label: "Mountain Time (MT) — Denver" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT) — Los Angeles" },
  { value: "America/Anchorage", label: "Alaska Time — Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii Time — Honolulu" },
  { value: "America/Toronto", label: "Eastern Time — Toronto" },
  { value: "America/Vancouver", label: "Pacific Time — Vancouver" },
  { value: "America/Mexico_City", label: "Central Time — Mexico City" },
  { value: "America/Bogota", label: "Colombia Time — Bogotá" },
  { value: "America/Lima", label: "Peru Time — Lima" },
  { value: "America/Santiago", label: "Chile Time — Santiago" },
  { value: "America/Buenos_Aires", label: "Argentina Time — Buenos Aires" },
  { value: "America/Sao_Paulo", label: "Brasília Time — São Paulo" },
  { value: "Europe/London", label: "Greenwich Mean Time — London" },
  { value: "Europe/Paris", label: "Central European Time — Paris" },
  { value: "Europe/Berlin", label: "Central European Time — Berlin" },
  { value: "Europe/Madrid", label: "Central European Time — Madrid" },
  { value: "Europe/Rome", label: "Central European Time — Rome" },
  { value: "Europe/Amsterdam", label: "Central European Time — Amsterdam" },
  { value: "Europe/Lisbon", label: "Western European Time — Lisbon" },
  { value: "Europe/Athens", label: "Eastern European Time — Athens" },
  { value: "Europe/Istanbul", label: "Turkey Time — Istanbul" },
  { value: "Africa/Cairo", label: "Eastern European Time — Cairo" },
  { value: "Africa/Lagos", label: "West Africa Time — Lagos" },
  { value: "Asia/Dubai", label: "Gulf Standard Time — Dubai" },
  { value: "Asia/Kolkata", label: "India Standard Time — Mumbai" },
  { value: "Asia/Bangkok", label: "Indochina Time — Bangkok" },
  { value: "Asia/Singapore", label: "Singapore Time — Singapore" },
  { value: "Asia/Shanghai", label: "China Standard Time — Shanghai" },
  { value: "Asia/Tokyo", label: "Japan Standard Time — Tokyo" },
  { value: "Asia/Seoul", label: "Korea Standard Time — Seoul" },
  { value: "Australia/Sydney", label: "Australian Eastern Time — Sydney" },
  { value: "Pacific/Auckland", label: "New Zealand Time — Auckland" },
];
